<template>
  <div class="voices-view">
    <BreadcrumbBar
      :parentName="station?.name || 'Station'"
      currentName="Voices"
      :backRoute="station ? `/station/${station.slug}` : '/'"
    />

    <div class="content">
      <!-- Toolbar -->
      <div class="content-toolbar">
        <div class="toolbar-left">
          <div class="search-box">
            <AppIcon name="search" />
            <input
              type="text"
              v-model="searchQuery"
              placeholder="Search voices..."
              @input="filterVoices"
            />
            <button v-if="searchQuery" class="btn-clear-search" @click="clearSearch">
              <AppIcon name="times" />
            </button>
          </div>
        </div>
        <div class="toolbar-right">
          <button class="btn-primary" @click="openAddVoice" :disabled="!elevenLabsConfigured">
            <AppIcon name="plus" /> Add Voice
          </button>
        </div>
      </div>

      <!-- ElevenLabs not configured warning -->
      <div v-if="!elevenLabsConfigured && !loading" class="empty-state">
        <AppIcon name="microphone" />
        <p>ElevenLabs is not configured</p>
        <p class="empty-hint">Go to <router-link to="/settings">Settings → AI</router-link> to add your ElevenLabs API key.</p>
      </div>

      <!-- Loading -->
      <div v-else-if="loading" class="loading-state">
        <AppIcon name="spinner" :spin="true" />
        <p>Loading voices...</p>
      </div>

      <!-- Empty -->
      <div v-else-if="filteredVoices.length === 0" class="empty-state">
        <AppIcon name="microphone" />
        <p v-if="searchQuery">No voices match "{{ searchQuery }}"</p>
        <p v-else>No voices yet</p>
        <p class="empty-hint" v-if="!searchQuery">Clone a DJ voice from an export to get started.</p>
      </div>

      <!-- Voice cards -->
      <div v-else class="voices-grid">
        <div v-for="voice in filteredVoices" :key="voice._id" class="voice-card">
          <div class="voice-card-header">
            <div class="voice-avatar">
              <AppIcon name="microphone" />
            </div>
            <div class="voice-info">
              <h3 class="voice-name">{{ voice.name }}</h3>
              <p class="voice-source">From: {{ voice.exportTitle || 'Unknown export' }}</p>
              <p class="voice-date">
                {{ formatDate(voice.createdAt) }}
                <span v-if="voice.generationText" class="voice-has-audio" title="Has generated audio">· 🔊 Audio ready</span>
              </p>
            </div>
          </div>
          <div class="voice-card-actions">
            <button class="btn-action btn-generate" @click="openGenerate(voice)" title="Generate Audio">
              <AppIcon name="play" /> Generate
            </button>
            <button class="btn-action btn-delete" @click="confirmDelete(voice)" title="Delete Voice">
              <AppIcon name="trash" />
            </button>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="hasMore || hasPrev" class="pagination-bar">
        <button class="btn-secondary" @click="loadPrev" :disabled="!hasPrev || loading">
          <AppIcon name="arrow-left" /> Newer
        </button>
        <button class="btn-secondary" @click="loadMore" :disabled="!hasMore || loading">
          Older <AppIcon name="arrow-right" />
        </button>
      </div>
    </div>

    <!-- ── Add Voice Modal ─────────────────────────────────────── -->
    <div v-if="addModal.show" class="modal-overlay" @click.self="closeAddVoice">
      <div class="modal voices-modal">
        <div class="modal-header">
          <h2>Add Voice</h2>
          <button class="btn-close-modal" @click="closeAddVoice"><AppIcon name="times" /></button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>Voice Name</label>
            <input type="text" v-model="addModal.name" placeholder="e.g. Carlos - Morning Show" class="form-input" />
          </div>
          <div class="form-group">
            <label>Select Export (source audio)</label>
            <div class="search-box export-search">
              <AppIcon name="search" />
              <input type="text" v-model="addModal.exportSearch" placeholder="Search exports..." @input="searchExports" class="form-input" />
            </div>
            <p class="export-limit-note">
              <AppIcon name="info-circle" /> Only exports under 5 minutes are shown. Longer audio doesn't improve voice quality.
            </p>
            <div class="export-picker">
              <div v-if="addModal.loadingExports" class="picker-loading"><AppIcon name="spinner" :spin="true" /> Loading...</div>
              <div v-else-if="addModal.exports.length === 0" class="picker-empty">No eligible exports found (under 5 min)</div>
              <div
                v-else
                v-for="exp in addModal.exports"
                :key="exp.id"
                :class="['export-pick-row', { selected: addModal.selectedExport?.id === exp.id }]"
                @click="addModal.selectedExport = exp"
              >
                <AppIcon name="file-audio" />
                <div class="export-pick-info">
                  <span class="export-pick-title">{{ exp.title || exp.filename }}</span>
                  <span class="export-pick-meta">{{ formatDuration(exp.duration) }} · {{ formatDate(exp.createdAt) }}</span>
                </div>
                <AppIcon v-if="addModal.selectedExport?.id === exp.id" name="check-circle" class="pick-check" />
              </div>
            </div>
          </div>
          <div v-if="addModal.error" class="form-error"><AppIcon name="exclamation-circle" /> {{ addModal.error }}</div>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="closeAddVoice">Cancel</button>
          <button class="btn-primary" @click="cloneVoice" :disabled="addModal.cloning || !addModal.name || !addModal.selectedExport">
            <AppIcon :name="addModal.cloning ? 'spinner' : 'microphone'" :spin="addModal.cloning" />
            {{ addModal.cloning ? 'Cloning...' : 'Clone Voice' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── Generate Audio Modal ────────────────────────────────── -->
    <div v-if="genModal.show" class="modal-overlay" @click.self="closeGenerate">
      <div class="modal voices-modal voices-modal-lg">
        <div class="modal-header">
          <h2>Generate Audio — {{ genModal.voice?.name }}</h2>
          <button class="btn-close-modal" @click="closeGenerate"><AppIcon name="times" /></button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label class="gen-label-row">
              <span>Script</span>
              <span :class="['char-count', genModal.text.length > 4500 ? 'char-warn' : '']">
                {{ genModal.text.length }} / 5000 chars
              </span>
            </label>
            <textarea
              v-model="genModal.text"
              class="form-input gen-textarea"
              placeholder="Type the script you want the voice to read..."
              maxlength="5000"
              rows="6"
            ></textarea>
          </div>
          <div v-if="genModal.error" class="form-error"><AppIcon name="exclamation-circle" /> {{ genModal.error }}</div>

          <!-- Existing audio -->
          <div v-if="genModal.hasExisting && genModal.audioBlob" class="gen-player">
            <audio :src="genModal.audioBlob" controls class="gen-audio-el"></audio>
            <a :href="genModal.audioBlob" :download="genModal.downloadName" class="btn-secondary btn-download">
              <AppIcon name="download" /> Download
            </a>
          </div>
          <div v-if="genModal.hasExisting && genModal.text !== (genModal.voice?.generationText || '')" class="gen-overwrite-warn">
            <AppIcon name="exclamation-triangle" /> Generating will replace the existing audio
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="closeGenerate">Close</button>
          <button class="btn-primary" @click="generateAudio" :disabled="genModal.generating || !genModal.text.trim()">
            <AppIcon :name="genModal.generating ? 'spinner' : 'play'" :spin="genModal.generating" />
            {{ genModal.generating ? 'Generating...' : 'Generate' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── Delete Confirm Modal ────────────────────────────────── -->
    <div v-if="deleteModal.show" class="modal-overlay" @click.self="deleteModal.show = false">
      <div class="modal modal-sm">
        <div class="modal-header">
          <h2>Delete Voice</h2>
          <button class="btn-close-modal" @click="deleteModal.show = false"><AppIcon name="times" /></button>
        </div>
        <div class="modal-body">
          <p>Delete <strong>{{ deleteModal.voice?.name }}</strong>? This will also remove the cloned voice from ElevenLabs and cannot be undone.</p>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="deleteModal.show = false">Cancel</button>
          <button class="btn-danger" @click="deleteVoice" :disabled="deleteModal.deleting">
            {{ deleteModal.deleting ? 'Deleting...' : 'Delete' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BreadcrumbBar from '../components/BreadcrumbBar.vue'
import AppIcon from '../components/AppIcon.vue'
import { authFetch } from '../services/api'

export default {
  name: 'VoicesView',
  components: { BreadcrumbBar, AppIcon },
  props: {
    slug: { type: String, required: true }
  },
  data() {
    return {
      station: null,
      voices: [],
      filteredVoices: [],
      searchQuery: '',
      loading: true,
      elevenLabsConfigured: false,
      hasMore: false,
      hasPrev: false,
      cursor: null,
      prevCursor: null,
      addModal: {
        show: false,
        name: '',
        exportSearch: '',
        exports: [],
        selectedExport: null,
        loadingExports: false,
        cloning: false,
        error: null
      },
      genModal: {
        show: false,
        voice: null,
        text: '',
        generating: false,
        audioUrl: null,
        downloadName: 'generated.mp3',
        error: null
      },
      deleteModal: {
        show: false,
        voice: null,
        deleting: false
      },
      exportSearchTimer: null
    }
  },
  async mounted() {
    await this.loadStation()
    await this.checkElevenLabs()
    await this.loadVoices()
  },
  beforeUnmount() {
    if (this.genModal.audioBlob) URL.revokeObjectURL(this.genModal.audioBlob)
  },
  methods: {
    async loadStation() {
      try {
        const res = await authFetch(`/api/stations/${this.slug}`)
        if (res.ok) this.station = await res.json()
      } catch (err) { console.error(err) }
    },
    async checkElevenLabs() {
      try {
        const res = await authFetch('/api/features')
        if (res.ok) {
          const data = await res.json()
          this.elevenLabsConfigured = data.elevenLabs || false
        }
      } catch (err) { console.error(err) }
    },
    async loadVoices(cursor = null) {
      this.loading = true
      try {
        let url = `/api/s/${this.slug}/voices?limit=12`
        if (cursor) url += `&before=${cursor}`
        const res = await authFetch(url)
        if (res.ok) {
          const data = await res.json()
          this.voices = data.voices
          this.filteredVoices = this.voices
          this.hasMore = data.hasMore
          this.hasPrev = data.hasPrev
          this.cursor = data.voices.length > 0 ? data.voices[data.voices.length - 1].createdAt : null
        }
      } catch (err) { console.error(err) }
      finally { this.loading = false }
    },
    loadMore() { this.loadVoices(this.cursor) },
    loadPrev() { this.loadVoices(this.prevCursor) },
    filterVoices() {
      const q = this.searchQuery.toLowerCase()
      this.filteredVoices = q
        ? this.voices.filter(v => v.name.toLowerCase().includes(q) || (v.exportTitle || '').toLowerCase().includes(q))
        : this.voices
    },
    clearSearch() { this.searchQuery = ''; this.filterVoices() },

    // ── Add Voice ────────────────────────────────────────────────
    async openAddVoice() {
      this.addModal.show = true
      this.addModal.name = ''
      this.addModal.exportSearch = ''
      this.addModal.selectedExport = null
      this.addModal.error = null
      await this.fetchExports('')
    },
    closeAddVoice() { this.addModal.show = false },
    searchExports() {
      clearTimeout(this.exportSearchTimer)
      this.exportSearchTimer = setTimeout(() => this.fetchExports(this.addModal.exportSearch), 300)
    },
    async fetchExports(search) {
      this.addModal.loadingExports = true
      try {
        let url = `/api/s/${this.slug}/exports?limit=50&status=complete`
        if (search) url += `&search=${encodeURIComponent(search)}`
        const res = await authFetch(url)
        if (res.ok) {
          const data = await res.json()
          // Only show exports under 5 minutes (300000ms) — ElevenLabs voice cloning limit
          this.addModal.exports = (data.exports || []).filter(e => e.duration && e.duration <= 300000)
        }
      } catch (err) { console.error(err) }
      finally { this.addModal.loadingExports = false }
    },
    async cloneVoice() {
      if (!this.addModal.name || !this.addModal.selectedExport) return
      this.addModal.cloning = true
      this.addModal.error = null
      try {
        const res = await authFetch(`/api/s/${this.slug}/voices`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: this.addModal.name,
            exportId: this.addModal.selectedExport.id
          })
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data.error || 'Failed to clone voice')
        this.closeAddVoice()
        await this.loadVoices()
      } catch (err) { this.addModal.error = err.message }
      finally { this.addModal.cloning = false }
    },

    // ── Generate ─────────────────────────────────────────────────
    async openGenerate(voice) {
      this.genModal = {
        show: true,
        voice,
        text: voice.generationText || '',
        generating: false,
        hasExisting: !!voice.generationText,
        audioBlob: null,
        downloadName: `${voice.name}.mp3`,
        error: null
      }
      // Fetch existing audio as blob so the audio element can play it with auth
      if (voice.generationText) {
        try {
          const res = await authFetch(`/api/s/${this.slug}/voices/${voice._id}/audio`)
          if (res.ok) {
            const blob = await res.blob()
            this.genModal.audioBlob = URL.createObjectURL(blob)
          }
        } catch (e) { console.error('Failed to load audio:', e) }
      }
    },
    closeGenerate() {
      if (this.genModal.audioBlob) { URL.revokeObjectURL(this.genModal.audioBlob); this.genModal.audioBlob = null }
      this.genModal.show = false
    },
    async generateAudio() {
      if (!this.genModal.text.trim()) return
      this.genModal.generating = true
      this.genModal.error = null
      try {
        const res = await authFetch(`/api/s/${this.slug}/voices/${this.genModal.voice._id}/generate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: this.genModal.text })
        })
        if (!res.ok) {
          const err = await res.json()
          throw new Error(err.error || 'Generation failed')
        }
        // Fetch new audio as blob
        if (this.genModal.audioBlob) { URL.revokeObjectURL(this.genModal.audioBlob); this.genModal.audioBlob = null }
        const audioRes = await authFetch(`/api/s/${this.slug}/voices/${this.genModal.voice._id}/audio`)
        if (audioRes.ok) {
          const blob = await audioRes.blob()
          this.genModal.audioBlob = URL.createObjectURL(blob)
        }
        this.genModal.hasExisting = true
        // Update the voice in the list so card shows indicator
        const idx = this.voices.findIndex(v => v._id === this.genModal.voice._id)
        if (idx >= 0) {
          this.voices[idx] = { ...this.voices[idx], generationText: this.genModal.text, generatedAt: new Date() }
          this.filterVoices()
        }
      } catch (err) { this.genModal.error = err.message }
      finally { this.genModal.generating = false }
    },

    // ── Delete ───────────────────────────────────────────────────
    confirmDelete(voice) {
      this.deleteModal = { show: true, voice, deleting: false }
    },
    async deleteVoice() {
      this.deleteModal.deleting = true
      try {
        const res = await authFetch(`/api/s/${this.slug}/voices/${this.deleteModal.voice._id}`, { method: 'DELETE' })
        if (res.ok) {
          this.deleteModal.show = false
          await this.loadVoices()
        }
      } catch (err) { console.error(err) }
      finally { this.deleteModal.deleting = false }
    },

    // ── Helpers ──────────────────────────────────────────────────
    formatDate(d) {
      if (!d) return ''
      return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    },
    formatDuration(ms) {
      if (!ms) return '0:00'
      const s = Math.floor(ms / 1000)
      return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
    }
  }
}
</script>

<style>
/* ── Layout — matches ExportsView exactly ───────────── */
.voices-view {
  min-height: calc(100vh - 74px);
  background: var(--gray-50);
}
.voices-view .content {
  padding: 32px;
  max-width: 1400px;
  margin: 0 auto;
}
.voices-view .content-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}
.voices-view .toolbar-left {
  display: flex;
  align-items: center;
  gap: 12px;
}
.voices-view .toolbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Search box — matches ExportsView .search-box exactly */
.voices-view .search-box {
  position: relative;
  display: flex;
  align-items: center;
}
.voices-view .search-box > svg {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--gray-400);
  pointer-events: none;
}
.voices-view .search-box input {
  padding: 8px 32px 8px 32px;
  border: 1px solid var(--gray-200);
  border-radius: 4px;
  font-size: 13px;
  width: 100%;
  background: var(--white);
}
.voices-view .search-box input:focus {
  outline: none;
  border-color: var(--gray-400);
}
.voices-view .btn-clear-search {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: var(--gray-400);
  padding: 0;
  display: flex;
}

/* Buttons — match app globals */
.voices-view .btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  background: var(--accent);
  border: none;
  border-radius: 4px;
  color: white;
  cursor: pointer;
}
.voices-view .btn-primary:hover { background: var(--accent-hover); }
.voices-view .btn-primary:disabled { opacity: 0.45; cursor: not-allowed; }

.voices-view .btn-secondary {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  background: white;
  border: 1px solid var(--gray-200);
  border-radius: 4px;
  color: var(--gray-600);
  cursor: pointer;
}
.voices-view .btn-secondary:hover { background: var(--gray-50); }

.voices-view .btn-danger {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  background: #dc2626;
  border: none;
  border-radius: 4px;
  color: white;
  cursor: pointer;
}
.voices-view .btn-danger:hover { background: #b91c1c; }
.voices-view .btn-danger:disabled { opacity: 0.45; cursor: not-allowed; }

.voices-view .btn-action {
  width: 32px;
  height: 32px;
  background: none;
  border: 1px solid var(--gray-200);
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--gray-600);
}
.voices-view .btn-action:hover { background: var(--gray-50); border-color: var(--gray-300); }
.voices-view .btn-action.btn-delete:hover { background: #fef2f2; border-color: #fecaca; color: #dc2626; }

.voices-view .btn-generate {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  font-size: 13px;
  font-weight: 500;
  background: var(--accent);
  border: none;
  border-radius: 4px;
  color: white;
  cursor: pointer;
  flex: 1;
  justify-content: center;
}
.voices-view .btn-generate:hover { background: var(--accent-hover); }

/* Voice grid */
.voices-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}
.voice-card {
  background: var(--white);
  border: 1px solid var(--gray-200);
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.voice-card:hover { box-shadow: 0 1px 8px rgba(0,0,0,0.06); }
.voice-card-header { display: flex; gap: 12px; align-items: flex-start; }
.voice-avatar {
  width: 40px; height: 40px;
  border-radius: 8px;
  background: linear-gradient(135deg, #f5a623, #e8820c);
  display: flex; align-items: center; justify-content: center;
  color: white; font-size: 16px; flex-shrink: 0;
}
.voice-info { flex: 1; min-width: 0; }
.voice-name { font-size: 14px; font-weight: 600; margin: 0 0 3px; color: var(--gray-900); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.voice-source { font-size: 12px; color: var(--gray-500); margin: 0 0 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.voice-date { font-size: 11px; color: var(--gray-400); margin: 0; }
.voice-card-actions { display: flex; gap: 8px; align-items: center; }

/* Modal */
.voices-view .modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.voices-view .modal {
  background: var(--white);
  border-radius: 12px;
  width: 100%;
  max-width: 520px;
  box-shadow: var(--shadow-lg);
  overflow: hidden;
}
.voices-view .modal-lg { max-width: 600px; }
.voices-view .modal-sm { max-width: 420px; }
.voices-view .modal-header {
  padding: 20px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid var(--gray-100);
}
.voices-view .modal-header h2 { margin: 0; font-size: 16px; font-weight: 600; color: var(--gray-900); }
.voices-view .btn-close-modal { background: none; border: none; cursor: pointer; color: var(--gray-400); display: flex; padding: 4px; }
.voices-view .btn-close-modal:hover { color: var(--gray-600); }
.voices-view .modal-body { padding: 24px; display: flex; flex-direction: column; gap: 16px; max-height: 60vh; overflow-y: auto; }
.voices-view .modal-footer { padding: 16px 24px; border-top: 1px solid var(--gray-100); display: flex; gap: 8px; justify-content: flex-end; }

/* Form */
.voices-view .form-group { display: flex; flex-direction: column; gap: 6px; }
.voices-view .form-group label { font-size: 13px; font-weight: 500; color: var(--gray-700); }
.voices-view .form-input { border: 1px solid var(--gray-200); border-radius: 4px; padding: 8px 12px; font-size: 13px; outline: none; font-family: inherit; color: var(--gray-900); background: var(--white); }
.voices-view .form-input:focus { border-color: var(--accent); }
.voices-view .form-error { color: #dc2626; font-size: 13px; display: flex; align-items: center; gap: 6px; }

/* Export picker */
.voices-view .export-search { margin-bottom: 8px; width: 100%; }
.voices-view .export-search .search-box { width: 100%; }
.voices-view .export-search .search-box input { width: 100%; }
.voices-view .export-picker { border: 1px solid var(--gray-200); border-radius: 4px; max-height: 220px; overflow-y: auto; }
.voices-view .export-pick-row { display: flex; align-items: center; gap: 10px; padding: 10px 14px; cursor: pointer; border-bottom: 1px solid var(--gray-100); transition: background 0.1s; }
.voices-view .export-pick-row:last-child { border-bottom: none; }
.voices-view .export-pick-row:hover { background: var(--gray-50); }
.voices-view .export-pick-row.selected { background: #eff4ff; }
.voices-view .export-pick-info { flex: 1; min-width: 0; }
.voices-view .export-pick-title { display: block; font-size: 13px; font-weight: 500; color: var(--gray-900); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.voices-view .export-pick-meta { font-size: 11px; color: var(--gray-400); }
.voices-view .pick-check { color: var(--accent); }
.voices-view .picker-loading, .voices-view .picker-empty { padding: 20px; text-align: center; color: var(--gray-400); font-size: 13px; display: flex; align-items: center; justify-content: center; gap: 8px; }

/* Generate modal */
.voices-view .gen-label-row { display: flex; justify-content: space-between; align-items: center; }
.voices-view .char-count { font-size: 12px; color: var(--gray-400); }
.voices-view .char-warn { color: #f59e0b; }
.voices-view .gen-textarea { width: 100%; resize: vertical; font-family: inherit; min-height: 120px; box-sizing: border-box; }
.voices-view .gen-player { display: flex; align-items: center; gap: 12px; padding: 12px; background: var(--gray-50); border-radius: 4px; border: 1px solid var(--gray-200); }
.voices-view .gen-audio-el { flex: 1; height: 36px; }

/* Pagination */
.voices-view .pagination-bar { display: flex; justify-content: center; gap: 12px; margin-top: 24px; }

/* Empty / loading */
.voices-view .empty-state { text-align: center; padding: 60px 20px; color: var(--gray-400); }
.voices-view .empty-state p { margin: 8px 0 0; font-size: 14px; color: var(--gray-600); }
.voices-view .empty-hint { font-size: 13px !important; color: var(--gray-400) !important; }
.voices-view .empty-state a { color: var(--accent); }
.export-limit-note {
  font-size: 12px;
  color: var(--gray-500);
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 8px;
  background: var(--gray-50);
  border: 1px solid var(--gray-200);
  border-radius: 4px;
  padding: 8px 10px;
}

.voice-has-audio { color: var(--accent); font-size: 11px; }
.voices-view .gen-overwrite-warn { font-size: 12px; color: #f59e0b; display: flex; align-items: center; gap: 6px; background: #fffbeb; border: 1px solid #fde68a; border-radius: 4px; padding: 8px 12px; }
.voices-view .loading-state { text-align: center; padding: 60px 20px; color: var(--gray-400); display: flex; align-items: center; justify-content: center; gap: 8px; font-size: 14px; }
</style>